import React, { useRef, useState } from "react";
import { ArrowRight, Sparkles, ArrowDown, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { handleStartEmpireFree, CTA_PRIMARY, CTA_PRIMARY_SUB } from "@/lib/cta";

const VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_4de35adf-276c-46b8-87e3-30ca763560ff/artifacts/tydthwfp_ClydeFlow_2_with_captions.mp4";
const POSTER_URL =
  "https://customer-assets.emergentagent.com/job_automation-empire/artifacts/mql0jwy4_flow-logo.png";

const Hero = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const start = () => handleStartEmpireFree(user, navigate);

  return (
    <section
      className="relative pt-32 pb-16 lg:pt-36 lg:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 -z-10 cf-grain" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-[#050B14]/30 to-[#050B14]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* LEFT — copy + CTA */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 border border-cyan-400/30 bg-cyan-400/5 text-cyan-300" data-testid="hero-badge">
            <Sparkles size={14} className="text-cyan-300" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em]">
              Execution Over Ideation · 14-Day Trial · No Card
            </span>
          </div>

          <h1 className="font-heading font-black text-white tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            Automate your{" "}
            <span className="bg-gradient-to-r from-[#00F0FF] via-[#38bdf8] to-[#10b981] bg-clip-text text-transparent">
              Empire.
            </span>
            <br />
            Own the <span className="italic font-light text-cyan-300">Flow.</span>
          </h1>

          <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="hero-subheadline">
            ClydeFlow is the AI automation suite that actually <em>does</em> the
            work. Built in Glasgow for solo-founders and local business owners.
            We close DMs. Ship blogs. Sync ads. Get on with it.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch">
            <button
              data-testid="hero-cta-trial"
              onClick={start}
              className="group relative px-7 py-4 bg-[#00F0FF] text-[#050B14] font-bold hover:bg-[#33F3FF] transition-all rounded-sm shadow-[0_0_40px_rgba(0,240,255,0.55)] flex flex-col items-center justify-center gap-0.5"
            >
              <span className="flex items-center gap-2 uppercase tracking-wider text-base">
                {CTA_PRIMARY}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </span>
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] opacity-80">
                {CTA_PRIMARY_SUB}
              </span>
              {/* Pulsing 'point here' arrow */}
              <ArrowDown
                size={28}
                className="absolute -top-8 right-3 text-cyan-300 animate-bounce hidden lg:block"
                aria-hidden="true"
              />
            </button>
            <a
              href="#pricing"
              data-testid="hero-cta-mogul"
              className="px-7 py-4 bg-transparent border border-white/20 text-white font-bold hover:bg-white/5 hover:border-white/40 transition-all rounded-sm flex items-center justify-center"
            >
              Get the Mogul Deal · £299
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-[0.2em] text-slate-400">
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />Meta-Verified DM API</span>
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />50 DMs + 2 Blogs Free</span>
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse" />Built in Glasgow</span>
          </div>
        </div>

        {/* RIGHT — founder video */}
        <div className="lg:col-span-5 relative" data-testid="hero-video-wrap">
          <div className="relative aspect-[9/16] max-h-[560px] mx-auto rounded-sm overflow-hidden border border-cyan-400/30 cf-glow bg-[#0A192F]">
            <video
              ref={videoRef}
              src={VIDEO_URL}
              autoPlay
              loop
              muted={muted}
              playsInline
              preload="auto"
              poster={POSTER_URL}
              className="w-full h-full object-cover"
              data-testid="hero-founder-video"
            />
            {/* Mute toggle */}
            <button
              onClick={() => setMuted((m) => !m)}
              data-testid="hero-video-mute"
              className="absolute bottom-3 right-3 p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm border border-white/20 text-white rounded-sm"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            {/* Live badge */}
            <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2 py-1 bg-black/70 backdrop-blur-sm border border-cyan-400/40 rounded-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-cyan-200">
                Founder
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px cf-ticker" />
    </section>
  );
};

export default Hero;
